import { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsPlus, BsPlusCircle, BsInfoCircle } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useError } from '#globalContexts/ErrorContext';

import { useSearchPreview } from '../../../../../hooks/useSearchPreview';
import { useTargetingVocabulary } from '../../../../../models/targeting/queries';
import { validateSearchFilters } from '../../../../../utils/validateSearchFilters';
import { CollapseCard, TypeBadge } from '../../../LeadCriteriaModal/CollapseCard';
import { RangeSliderWithInput } from '../../../LeadCriteriaModal/criteriaFormAtoms';
import { SearchCriteriaCards } from '../../../LeadCriteriaModal/SearchCriteriaCards';
import { SearchPreview } from '../../../LeadCriteriaModal/SearchPreview';

import { useExtendSearchFilters } from './useExtendSearchFilters';

const DEFAULT_ADDITIONAL_RESULTS = 50;
const MAX_ADDITIONAL_RESULTS = 500;
const QUICK_SELECT_OPTIONS = [25, 50, 100, 200];

const fmtCount = (n) => Math.round(n || 0).toLocaleString('de-DE');

const AdditionalResultsCard = ({ value, onChange }) => {
	const [open, setOpen] = useState(true);

	return (
		<CollapseCard
			icon={BsPlusCircle}
			title="Zusätzliche Leads"
			summary={`+${value}`}
			open={open}
			onToggle={() => setOpen((o) => !o)}
		>
			<RangeSliderWithInput
				min={1}
				max={MAX_ADDITIONAL_RESULTS}
				value={value}
				onChange={onChange}
				label="Anzahl zusätzlicher Ergebnisse"
			/>
			<div className="grid grid-cols-4 gap-2">
				{QUICK_SELECT_OPTIONS.map((option) => (
					<button
						key={option}
						type="button"
						onClick={() => onChange(option)}
						className={`px-2 py-1.5 text-xs font-medium rounded-md border transition-colors ${
							value === option
								? 'bg-primary-100 border-primary-300 text-primary-700'
								: 'bg-white border-gray-200 text-text hover:bg-gray-50'
						}`}
					>
						+{option}
					</button>
				))}
			</div>
		</CollapseCard>
	);
};

AdditionalResultsCard.propTypes = {
	value: PropTypes.number.isRequired,
	onChange: PropTypes.func.isRequired,
};

const SummaryRow = ({ label, value, emphasized = false }) => (
	<div className="flex items-baseline justify-between gap-2 text-sm">
		<span className="text-bgBase-800">{label}</span>
		<span className={`tabular-nums ${emphasized ? 'font-bold text-primary-700' : 'text-text'}`}>{value}</span>
	</div>
);

SummaryRow.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.string.isRequired,
	emphasized: PropTypes.bool,
};

/**
 * Extends an existing prospect query with more results. Mirrors the "Suchkriterien" popup, prefilled with
 * the query's stored criteria; the search type is fixed because an extension keeps its query's type.
 * Changed criteria replace the stored query input, so later extensions start from them.
 */
const ExtendScrapingModal = ({
	isOpen,
	onClose,
	onConfirm,
	currentCount = 0,
	queryId = null,
	queryName = '',
	queryInput = null,
	queryTypeCode = '',
}) => {
	const { showError } = useError();
	const [additionalResults, setAdditionalResults] = useState(DEFAULT_ADDITIONAL_RESULTS);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { filters, searchType, isGermany, hasChanges, handleFilterChange, getModifiedSearchParameters } =
		useExtendSearchFilters(queryInput, queryTypeCode, isOpen);

	const preview = useSearchPreview({ filters, searchType, isGermany, enabled: isOpen });
	const { data: targetingVocabulary } = useTargetingVocabulary();

	useEffect(() => {
		if (isOpen) {
			setAdditionalResults(DEFAULT_ADDITIONAL_RESULTS);
			setIsSubmitting(false);
		}
	}, [isOpen]);

	if (!isOpen) return null;

	const handleClose = () => {
		if (!isSubmitting) onClose();
	};

	const handleConfirm = async () => {
		// Unchanged criteria are not re-sent, so legacy stored inputs stay extendable as they are
		const validationError = hasChanges
			? validateSearchFilters(filters, searchType, targetingVocabulary?.limits)
			: null;
		if (validationError) {
			showError(validationError);
			return;
		}

		setIsSubmitting(true);
		try {
			const result = await onConfirm(additionalResults, getModifiedSearchParameters());
			if (result) onClose();
		} catch {
			// onConfirm reports its own failures; the modal stays open for a retry
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="xl">
			<Panel.Header icon={BsPlus} subtitle={queryName} onClose={handleClose}>
				Suche erweitern
			</Panel.Header>

			{/* Same column layout as the "Suchkriterien" popup: each column scrolls on its own. */}
			<Panel.Body padded={false} scroll={false} className="bg-gray-50 flex flex-col">
				<div className="flex-1 min-h-0 grid grid-cols-12 grid-rows-[minmax(0,1fr)] gap-5 p-6">
					<div className="col-span-12 lg:col-span-8 min-h-0 overflow-y-auto grid grid-cols-2 gap-4 gap-x-4 content-start pr-1">
						<SearchCriteriaCards
							filters={filters}
							searchType={searchType}
							isGermany={isGermany}
							hasActiveHRFilters={false}
							hasActiveMapsFilters={false}
							onFilterChange={handleFilterChange}
							lockedSearchType={searchType}
							currentQueryId={queryId}
						>
							<AdditionalResultsCard value={additionalResults} onChange={setAdditionalResults} />
						</SearchCriteriaCards>
					</div>

					<div className="col-span-12 lg:col-span-4 flex flex-col gap-4 lg:min-h-0 lg:overflow-y-auto">
						<SearchPreview
							searchType={searchType}
							hasCriteria={preview.hasCriteria}
							isLoading={preview.isLoading}
							isResolving={preview.isResolvingIndustry}
							data={preview.data}
							companyKinds={filters.companyKinds}
							onCompanyKindsChange={(kinds) => handleFilterChange('companyKinds', kinds)}
						/>

						<div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mt-auto space-y-3">
							<div className="flex items-center justify-between gap-2">
								<TypeBadge type={searchType} />
								<span className="text-[11px] text-bgBase-800">Suchtyp bleibt erhalten</span>
							</div>
							<div className="space-y-1.5">
								<SummaryRow label="Bereits in der Suche" value={fmtCount(currentCount)} />
								<SummaryRow label="Zusätzlich gesucht" value={`+${fmtCount(additionalResults)}`} />
								<SummaryRow
									label="Danach bis zu"
									value={fmtCount(currentCount + additionalResults)}
									emphasized
								/>
							</div>
							{hasChanges && (
								<div className="p-2 rounded-md bg-primary-100 text-[11px] text-primary-700 flex items-start gap-2">
									<BsInfoCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
									<span>
										Kriterien angepasst. Die neuen Kriterien werden für diese Suche gespeichert und
										gelten auch für spätere Erweiterungen.
									</span>
								</div>
							)}
							<Button onClick={handleConfirm} loading={isSubmitting} variant="solid" size="md" fullWidth>
								<BsPlus className="w-5 h-5" />
								Suche erweitern
							</Button>
							<Button
								onClick={() => handleFilterChange('reset')}
								disabled={!hasChanges || isSubmitting}
								variant="outline"
								size="md"
								fullWidth
							>
								Kriterien zurücksetzen
							</Button>
						</div>
					</div>
				</div>
			</Panel.Body>
		</Modal>
	);
};

ExtendScrapingModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	currentCount: PropTypes.number,
	queryId: PropTypes.string,
	queryName: PropTypes.string,
	queryInput: PropTypes.object,
	queryTypeCode: PropTypes.string,
};

export default ExtendScrapingModal;
